import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { TokenExpiryService } from '../../../shared/services/token-expiry.service';
import { TokenRefreshService } from '../../../shared/services/token-refresh.service';
import { isBackendRequest } from './auth-token.interceptor';
import { apiPaths } from '../constants/api.const';

/**
 * Last line of defense for a rejected token: renew once, retry, and only then prompt.
 *
 * The backend answers an expired or invalid access token with 401 and answers an unknown
 * user with 404, so a 401 always means the token this request carried was not accepted.
 * That is usually recoverable — a token can lapse between being attached and reaching the
 * server — so a forced renewal is tried before the operator is involved.
 *
 * The error still propagates once recovery fails, so callers keep their own handling.
 */
@Injectable()
export class AuthExpiryInterceptor implements HttpInterceptor {

  constructor(
    private tokenExpiry: TokenExpiryService,
    private tokenRefresh: TokenRefreshService
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error?.status !== 401 || this.isCommitReauth(req.url)) {
          return throwError(() => error);
        }

        // Nothing of ours to renew on a URL outside the backend.
        if (!isBackendRequest(req.url)) {
          this.tokenExpiry.promptReauthentication();
          return throwError(() => error);
        }

        return this.retryWithFreshToken(req, next, error);
      })
    );
  }

  /**
   * Renews the token and replays the request once.
   *
   * The replay goes to `next`, downstream of both this interceptor and the one that
   * normally attaches the token. So the header is set here, and the replay's own failure
   * is caught here too — it cannot come back round through the block above, which is also
   * what stops this from looping.
   */
  private retryWithFreshToken(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    return from(this.tokenRefresh.acquire(true)).pipe(
      switchMap(outcome => {
        if (outcome.status !== 'refreshed') {
          this.tokenExpiry.promptReauthentication();
          return throwError(() => error);
        }

        const replay = req.clone({
          setHeaders: { Authorization: `Bearer ${outcome.accessToken}` }
        });

        return next.handle(replay).pipe(
          catchError((replayError: HttpErrorResponse) => {
            // Rejected even with a token minted seconds ago: the session itself is gone.
            if (replayError?.status === 401) {
              this.tokenExpiry.promptReauthentication();
            }
            return throwError(() => replayError);
          })
        );
      })
    );
  }

  /**
   * Commit sends a separate, freshly acquired ID token for the server to verify, and
   * rejects it with its own 401. That is a failed commit, not a lapsed session, so it
   * must not pull up the session dialog.
   */
  private isCommitReauth(url: string | null | undefined): boolean {
    return !!url && url.includes(apiPaths.study.commitStudy);
  }
}
